import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { RowNode } from 'ag-grid-community';
import SweetAlert2 from 'sweetalert2';

import { NSMessages } from '@ruby/configs/messages.constants';
import { NSTrackMoveConstants } from '@ruby/configs/track-move.constants';
import { ICustomer } from '@ruby/modules/customer-select/models/customer.interface';
import { ITrackInventory, ITrackMovePayload } from '@ruby/modules/track-move/models/track-move.interface';
import { EColors } from '@ruby/shared/enums/colors.enums';
import { ETrackAction } from '@ruby/shared/enums/track-move.enums';
import { ITrack } from '@ruby/shared/models/inventory-management/inventory-header.interface';
import { InventoryManagementUtils } from '@ruby/shared/services/utils/inventory-management/inventory-management.utils';

@Component({
  selector: 'ng-to-track-header',
  templateUrl: './to-track-header.component.html',
  styleUrls: ['./to-track-header.component.scss']
})
export class ToTrackHeaderComponent implements OnChanges {
  @Input() fromTrackGrid!: AgGridAngular;
  @Input() toTrackGrid!: AgGridAngular;
  @Input() customerSelected!: ICustomer;
  @Input() selectedFromTrack?: ITrack;
  @Input() selectedToTrack?: ITrack;
  @Input() fromTrackData!: Array<ITrackInventory>;
  @Input() toTrackData!: Array<ITrackInventory>;
  @Output() adjustTrack = new EventEmitter<{ fromTrackEquipments: Array<string>; toTrackEquipments: Array<string>; payload: ITrackMovePayload }>();

  constructor(private inventoryUtilsSvc: InventoryManagementUtils) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.toTrackGrid && changes.toTrackGrid.currentValue) {
      this.toTrackGrid.api.addEventListener('rowDataChanged', this.updateConfig.bind(this));
      this.toTrackGrid.api.addEventListener('selectionChanged', this.updateConfig.bind(this));
      this.toTrackGrid.api.addEventListener('rowDragEnd', this.rowDragEnd.bind(this));
      this.toTrackGrid.api.addEventListener('rowDataUpdated', this.updateConfig.bind(this));
    }
  }

  /**
   * Adjust
   *
   * @summary Validated selected tracks and emits adjust
   * @param $action: string
   * @return void
   */
  adjust($action: string): void {
    const itemsToUpdate: Array<ITrackInventory> = [];
    const toTrackEquipments: Array<string> = [];
    this.toTrackGrid.api.getSelectedNodes().forEach((node: RowNode) => {
      itemsToUpdate.push({ ...node.data, toTrack: undefined });
      toTrackEquipments.push(node.data.equipmentId);
    });
    this.adjustTrack.emit({
      toTrackEquipments,
      fromTrackEquipments: this.getFromTrackEquipments(),
      payload: {
        fromTrack: this.selectedFromTrack,
        toTrack: this.selectedToTrack,
        inventoryToMove: itemsToUpdate,
        stationCode: this.customerSelected.locations[0].stationCode,
        classCode: this.customerSelected.locations[0].classCode,
        action: $action as ETrackAction
      } as ITrackMovePayload
    });
  }

  /**
   * Flip Track
   *
   * @summary Validated selected tracks
   * @return void
   */
  flipTrack(): void {
    const lineNumbers: Array<number> = [];
    this.toTrackGrid.api.getSelectedNodes().forEach((node: RowNode) => {
      lineNumbers.push(node.data.lineNumber);
    });
    const dontAllow: boolean = this.inventoryUtilsSvc.validLineNumbers(lineNumbers);
    if (dontAllow) {
      SweetAlert2.fire({ title: 'Alert', text: NSMessages.consecutiveTrack, icon: 'warning', heightAuto: false, confirmButtonColor: EColors.GOLD });
    } else {
      this.adjust(ETrackAction.FLIP);
    }
  }

  /**
   * Update Config
   *
   * @summary Update's track action config
   * @return void
   */
  updateConfig(): void {
    this.toTrackGrid.context = this.inventoryUtilsSvc.resetTrackActions(
      this.toTrackGrid.api.getSelectedNodes(),
      this.selectedToTrack ? this.selectedToTrack.mainTrack : false,
      this.toTrackGrid.api.getDisplayedRowCount()
    );
  }

  /**
   * Row Drag End
   *
   * @summary Reset's track action config
   * @return void
   */
  rowDragEnd(): void {
    this.toTrackGrid.context = NSTrackMoveConstants.trackAction;
  }

  private getFromTrackEquipments(): Array<string> {
    const fromTrackEquipments: Array<string> = [];
    if (this.selectedFromTrack) {
      this.fromTrackGrid.api.getSelectedNodes().forEach((node: RowNode) => {
        fromTrackEquipments.push(node.data.equipmentId);
      });
    }
    this.resetGrids();
    return fromTrackEquipments;
  }

  private resetGrids(): void {
    this.toTrackGrid.api.setRowData([]);
    this.toTrackGrid.api.setRowData(this.toTrackData);
    this.fromTrackGrid.api.setRowData([]);
    this.fromTrackGrid.api.setRowData(this.fromTrackData);
  }
}
